import { HttpClient } from "@angular/common/http";
import * as moment from "moment-timezone";
import { BehaviorSubject, Observable } from "rxjs";
import {
  VenueDrinkPayoutSummaryBuilder,
  VenueDrinkTransactionsSummaryBuilder,
  VenueTopSellersSummaryBuilder
} from "../../export/export.service";
import { Order } from "../../order/order.model";
import { TimeframeFilter } from "../../timeframe/timeframes";

export class MenuSalesAnalytics {
  private venue: string;
  private timeframe: string;
  private units: number;
  private total: number;
  private tips: number;
  private subtotal: number;
  private topSellers: Map<string, TopSellersData>;
  private tipRecipients: Map<string, TipRecipientData>;

  private transactions: number;
  private serviceFees: number;

  private units$$: BehaviorSubject<number>;
  private transactions$$: BehaviorSubject<number>;
  private serviceFees$$: BehaviorSubject<number>;
  private avgPrice$$: BehaviorSubject<number>;
  private avgTip$$: BehaviorSubject<number>;
  private total$$: BehaviorSubject<number>;
  private subtotal$$: BehaviorSubject<number>;
  private tips$$: BehaviorSubject<number>;
  private topSellers$$: BehaviorSubject<TopSellersData[]>;
  private tipRecipients$$: BehaviorSubject<TipRecipientData[]>;

  private _orders: Map<string, Order>;

  public get units$(): Observable<number> {
    return this.units$$.asObservable();
  }

  public get avgPrice$(): Observable<number> {
    return this.avgPrice$$.asObservable();
  }

  public get total$(): Observable<number> {
    return this.total$$.asObservable();
  }
  public get tips$(): Observable<number> {
    return this.tips$$.asObservable();
  }

  public get avgTip$(): Observable<number> {
    return this.avgTip$$.asObservable();
  }

  public get topSellers$(): Observable<TopSellersData[]> {
    return this.topSellers$$.asObservable();
  }
  public setTimeframe(tf: TimeframeFilter) {
    this.timeframe =
      moment(tf.begin.toDate()).format("MM/DD/YY") +
      " - " +
      moment(tf.end.toDate()).format("MM/DD/YY");
  }

  constructor() {
    //By Order shown horizontally in summary
    this.units$$ = new BehaviorSubject<number>(0);
    this.avgPrice$$ = new BehaviorSubject<number>(0);
    this.total$$ = new BehaviorSubject<number>(0);
    this.subtotal$$ = new BehaviorSubject<number>(0);
    this.tips$$ = new BehaviorSubject<number>(0);
    this.avgTip$$ = new BehaviorSubject<number>(0);
    this.serviceFees$$ = new BehaviorSubject<number>(0);
    this.transactions$$ = new BehaviorSubject<number>(0);
    //Shown in table
    this.topSellers$$ = new BehaviorSubject<TopSellersData[]>([]);
    this.tipRecipients$$ = new BehaviorSubject<TipRecipientData[]>([]);
  }

  initialize() {
    this.units = 0;
    this.subtotal = 0;
    this.total = 0;
    this.serviceFees = 0;
    this.transactions = 0;
    this.tips = 0;
    this.topSellers = new Map();
    this.tipRecipients = new Map();
    this._orders = new Map();
  }

  next() {
    this.units$$.next(this.units);
    this.avgPrice$$.next(this.units == 0 ? 0 : this.subtotal / this.units);
    this.total$$.next(this.subtotal);
    this.tips$$.next(this.tips);
    this.avgTip$$.next(this.total == 0 ? 0 : this.tips / this.subtotal);
    this.topSellers$$.next(
      Array.from(this.topSellers.values()).sort((a, b) => b.total - a.total)
    );
    this.tipRecipients$$.next(
      Array.from(this.tipRecipients.values()).sort(
        (a, b) => b.subtotal - a.subtotal
      )
    );
    this.transactions$$.next(this.transactions);
    this.serviceFees$$.next(this.serviceFees);
    this.subtotal$$.next(this.subtotal);
  }

  addOrder(order: Order) {
    this._orders.set(order.reference.id, order);
    this.transactions += order.summary.subtotal != 0 ? 1.0 : 0;
    this.serviceFees += order.summary.serviceFee ?? 0;
    this.venue = order.venue.name;
    this.units += order.summary.units;
    this.total += order.summary.total;
    this.subtotal += order.summary.subtotal;
    this.tips += order.summary.tip;
    if (order.menu.tipRecipient != null) {
      if (this.tipRecipients.has(order.menu.tipRecipient)) {
        this.tipRecipients
          .get(order.menu.tipRecipient)
          .add(order.summary.subtotal, order.summary.units, order.summary.tip);
      } else {
        this.tipRecipients.set(
          order.menu.tipRecipient,
          new TipRecipientData(
            order.menu.tipRecipient,
            order.summary.subtotal,
            order.summary.tip,
            order.summary.units
          )
        );
      }
    }
    for (let i = 0; i < order.menu.items.length; i++) {
      const item = order.menu.items[i];
      this.topSellers.has(item.itemName)
        ? this.topSellers.get(item.itemName).add(item.units, item.itemPrice)
        : this.topSellers.set(
            item.itemName,
            new TopSellersData(
              item.itemName,
              item.categories.toString(),
              item.units,
              item.units * item.itemPrice
            )
          );
    }
  }

  toPayoutSummary(isTipDistributionActive: boolean): DrinkPayoutSummary {
    let summary = new DrinkPayoutSummary();
    summary.venue = this.venue;
    summary.timeframe = this.timeframe;
    summary.grossRevenue = this.subtotal$$.value;
    summary.grossTips = this.tips$$.value;
    summary.isTipDistributionActive = isTipDistributionActive;
    summary.tipRecipients = this.tipRecipients$$.value;
    summary.serviceFees = this.serviceFees$$.value;
    summary.transactions = this.transactions$$.value;
    return summary;
  }
  onVenuePayoutSummaryPressed(
    http: HttpClient,
    isTipDistributionActive: boolean
  ) {
    const summary = this.toPayoutSummary(isTipDistributionActive);
    let builder = new VenueDrinkPayoutSummaryBuilder(summary, http);
    builder.start();
  }

  onTopSellersSummaryPressed(http: HttpClient) {
    const summary = new TopSellersSummary(
      this.topSellers$$.value,
      this.venue,
      this.timeframe
    );
    let builder = new VenueTopSellersSummaryBuilder(summary, http);
    builder.start();
  }

  private get orders(): Order[] {
    return Array.from(this._orders.values()).sort((a, b) =>
      moment(a.summary.purchasedAt).isAfter(moment(b.summary.purchasedAt))
        ? 1
        : -1
    );
  }

  onTransactionsSummaryPressed(http: HttpClient) {
    let builder = new VenueDrinkTransactionsSummaryBuilder(
      new TransactionsSummary(this.orders, this.venue, this.timeframe),
      http
    );
    builder.start();
  }
}

export class TopSellersSummary {
  public topSellers: TopSellersData[];
  public venue: string;
  public timeframe: string;
  constructor(topSellers: TopSellersData[], venue: string, timeframe: string) {
    this.topSellers = topSellers;
    this.venue = venue;
    this.timeframe = timeframe;
  }
}

export class TransactionsSummary {
  constructor(
    public orders: Order[],
    public venue: string,
    public timeframe: string
  ) {
    //DO NOTHING
  }
}

export class DrinkPayoutSummary {
  public isTipDistributionActive: boolean;
  public venue: string;
  public timeframe: string;
  public transactions: number;
  public grossRevenue: number;
  public grossTips: number;

  public get revenueFees(): number {
    return this.braintreeFees;
  }

  public get braintreeFees(): number {
    return this.transactions * 0.3 + this.grossRevenue * 0.029;
  }

  public serviceFees: number;
  public tipRecipients: TipRecipientData[];

  constructor() {}

  public get tipFees(): number {
    return this.grossTips * 0.029;
  }
}

export class TopSellersData {
  public name: string;
  public categories: string;
  public units: number;
  public total: number;

  constructor(name: string, categories: string, units: number, total: number) {
    this.name = name;
    this.categories = categories;
    this.units = units;
    this.total = total;
  }

  add(units: number, price: number) {
    this.units += units;
    this.total += units * price;
  }

  public get avgPrice(): number {
    return this.total / this.units;
  }
}
export class TipRecipientData {
  public name: string;
  public subtotal: number;
  public tips: number;
  public units: number;
  public transactions: number;

  constructor(name: string, subtotal: number, tips: number, units: number) {
    this.name = name;
    this.transactions = 1;
    this.units = units;
    this.subtotal = subtotal;
    this.tips = tips;
  }

  add(subtotal: number, units: number, tips: number) {
    this.subtotal += subtotal;
    this.tips += tips;
    this.units += units;
    this.transactions += 1;
  }
  public get tipPercentage(): number {
    return this.tips / this.subtotal;
  }
  public get braintreeFees(): number {
    return this.tips * 0.029 + this.transactions * 0.3;
  }
}
